// import axios from "axios";
// let CancelToken = axios.CancelToken;
import axios from "axios";
import store from "@/store";
import authToken from "./authToken";
import moment from "moment";
import jwt_decode from 'jwt-decode';
import router from "./router";

import { handleUnauthorized, ingestResponse } from "./session";
// import { keys } from "@/helper";

let CancelToken = axios.CancelToken;
let source = CancelToken.source();

// Writes still waiting for an answer, keyed by the component that sent them
// plus exactly what they send. A double-click, a click while the request runs,
// or Enter pressed twice repeats the same write from the same component; that
// copy is not sent again. Only while in flight: the entry goes as soon as the
// first request ends, so a retry after a failure goes through as normal.
const inFlightWrites = new Map();

// Text that is equal for two identical bodies, or null when the body can't be
// described (then nothing is skipped). JSON.stringify turns a FormData into
// "{}", which would make every upload to one URL look alike, so a FormData is
// listed entry by entry and a file by its name, size and last change.
const describeBody = (data) => {
  try {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      const entries = [];
      data.forEach((value, key) => {
        if (typeof File !== "undefined" && value instanceof File) {
          value = `file:${value.name}:${value.size}:${value.lastModified}`;
        } else if (typeof Blob !== "undefined" && value instanceof Blob) {
          value = `blob:${value.size}:${value.type}`;
        }
        entries.push([key, value]);
      });
      return JSON.stringify(entries);
    }
    if (typeof Blob !== "undefined" && data instanceof Blob) return null;
    return JSON.stringify(data);
  } catch (e) {
    return null;
  }
};

axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";
// axios.defaults.baseURL = process.env.VUE_APP_BASE_URL;

// request interceptor
axios.interceptors.request.use(
  (req) => {
    if (req.headers.authorization) {
      const { accessToken, refreshToken } = authToken.getAccessToken();
      // console.log(accessToken);
      if (accessToken && refreshToken) {
        const refresh = jwt_decode(refreshToken);
        const isRefreshExpired = moment.unix(refresh.exp).diff(moment()) < 1;
        if (isRefreshExpired) {
          authToken.removeAccessToken();
          router.push({ name: "login" });
          return null;
        }
        return req;
      }
      return null;
    }
    return req;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// response interceptor
//
// Every response carries how long the dashboard session has left; the shared
// idle clock runs off it (app-js/session.js). A 401 on a signed-in request means
// the token stopped working -- logged out, idle past the NBFC's timeout, or the
// teammate was removed -- so sign out and say why. (There is no token refresh:
// staff tokens last a year, and the old refresh path here called an endpoint
// export that never existed.)
axios.interceptors.response.use(
  (response) => {
    ingestResponse(response);
    return response;
  },
  (error) => {
    // Optional chaining: a timeout or dropped connection has no response at all.
    if (error.response) ingestResponse(error.response);
    if (error.response?.status === 401 && handleUnauthorized(error)) {
      error.sessionHandled = true;
    }
    return Promise.reject(error);
  }
);

// let showToast = (snackbar) =>
//     store.dispatch("snackBar/showToast", snackbar);

// Keys whose message needs no "Field: " prefix.
const PLAIN_KEYS = ["", "detail", "message", "error", "non_field_errors"];

// The first readable message in an error body: a sentence, or the first entry of
// {field: message | [messages]} as "Field: message". Never the object itself --
// a toast given an object prints it as JSON.
function firstText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = firstText(item);
      if (text) return text;
    }
    return "";
  }
  if (typeof value === "object") {
    for (const key of Object.keys(value)) {
      const text = firstText(value[key]);
      if (!text) continue;
      if (PLAIN_KEYS.includes(key) || /^\d+$/.test(key)) return text;
      const label = key.replace(/_/g, " ");
      return `${label.charAt(0).toUpperCase()}${label.slice(1)}: ${text}`;
    }
  }
  return "";
}

/** What went wrong with a request, as one line a person can read. The server's
 * own words when it sent some; otherwise a plain line for the status -- never an
 * error page, a raw status code or JSON. `fallback` covers a refusal with no
 * words of its own. */
export function errorText(response, fallback = "") {
  if (!response) return "No connection to the server. Check the internet and try again.";
  const { status, data } = response;
  if (status >= 502 && status <= 504) {
    return "The server is not reachable right now. Try again in a minute.";
  }
  // A crash's own text is for the logs, not for the person who hit it.
  if (status >= 500) return "Something went wrong on our side. Try again in a minute.";
  const said =
    data && typeof data === "object"
      ? firstText(data.message) || firstText(data.detail) || firstText(data.error)
      : "";
  if (said) return said;
  if (status === 404) return "Not found.";
  if (status === 403) return "You don't have access to this.";
  return fallback || "That didn't work. Try again.";
}

const errorHandler = (error) => {
  // A request this app cancelled itself, because a newer one replaced it.
  if (axios.isCancel(error)) return;
  // The server refused an action this teammate's access no longer allows:
  // refresh access so buttons disable and tabs hide to match.
  if (
    error.response &&
    error.response.status === 403 &&
    error.response.data &&
    error.response.data.code === "permission_denied"
  ) {
    store.dispatch("access/fetchAccess", { force: true });
  }
  // No `error.response` means the request never got one: a timeout, a dropped
  // connection, a CORS rejection. Those get a toast too -- a silent failure is
  // how a lost NBFC profile call at login could leave a session with no Call
  // button and no clue why.
  const message =
    !error.response && error.code === "ECONNABORTED"
      ? "The server took too long to answer. Try again."
      : errorText(error.response);
  store.dispatch("snackBar/showToast", { message, color: "E" });
};

export default (
  method,
  url,
  {
    headers = {},
    params = {},
    data = {},
    lookUpKey = "",
    onSuccess = null,
    onFailure = null,
    onFinally = null,
    responseType = "json",
    onUploadProgress = null,
    onDownloadProgress = null,
    isTokenRequired = true,
    isNbfcRequired = true,
    cancel = false,
    showErrorMessage = true,  
    // A background request (a poller): never counts as the teammate's action.
    passive = false,
    // The component making the call, passed in by `this.$Axios` (main.js).
    owner = null,
    // Send even when the same write is in flight. For writes that are safe to
    // repeat and whose caller waits on onSuccess/onFailure to settle a promise:
    // a skipped copy never gets those, so its promise would never settle.
    allowDuplicate = false,
    // For a caller that settles its own promise from onSuccess/onFailure: a
    // skipped copy is handed the first request's outcome, so its promise settles
    // too (no second request, no second toast).
    settleDuplicate = false,
  } = {}
) => {
  const methods = ["get", "post", "patch", "put", "delete"];
  if (!methods.includes(method)) return `Method ${method} not allowed.`;
  if (!url) return "url is required";
  if (lookUpKey) url = url + `${lookUpKey}/`;

  let writeKey = null;
  if (owner && method !== "get" && !passive && !allowDuplicate) {
    const query = describeBody(params);
    const body = describeBody(data);
    if (query !== null && body !== null) {
      writeKey = `${owner._uid}|${method}|${url}|${query}|${body}`;
      const pending = inFlightWrites.get(writeKey);
      if (pending) {
        // The same write is already on its way. Skip this copy's success and
        // failure handling (no second toast, no second follow-up request) but
        // let its spinner stop when the first one ends.
        return pending
          .then((outcome) => {
            if (!settleDuplicate || !outcome) return;
            if (outcome.ok && onSuccess != null) onSuccess(outcome.response);
            if (!outcome.ok && onFailure != null) onFailure(outcome.response);
          })
          .finally(() => {
            if (onFinally != null) onFinally();
          });
      }
    }
  }

  const config = {
    method,
    url,
    lookUpKey,
    headers,
    params,
    data,
    responseType,
    onUploadProgress,
    onDownloadProgress,
  };

  if (cancel) {
    source.cancel();
    source = axios.CancelToken.source();
    config.cancelToken = source.token;
  }

  if (isTokenRequired) {
    const { accessToken } = authToken.getAccessToken();
    headers["authorization"] = `Bearer ${accessToken}`;
  }
  console.log("axios");
  
  if (isNbfcRequired) {
        const nbfcId = authToken.getNbfcId();
        // console.log("nbfcid", nbfcId);
        if (nbfcId) {
          headers["X-NBFC-ID"] = nbfcId;
        }
      }
  if (passive) {
    headers["X-Session-Passive"] = "1";
  }
  // headers['platform'] = keys.WEB;

  let sent = axios(config);
  if (writeKey) sent = sent.finally(() => inFlightWrites.delete(writeKey));

  // What a settleDuplicate copy of this write is handed when it ends.
  let outcome = null;
  const request = sent
    .then((response) => {
      outcome = { ok: true, response };
      if (onSuccess != null) {
        onSuccess(response);

        let display_message = response.data.message
          ? response.data.message
          : response.data.detail;
        if (config.method != "get")
          store.dispatch("snackBar/showToast", {
            message: display_message,
            color: config.method === "delete" ? "e" : "s",
          });
      }
    })
    .catch((error) => {
      outcome = { ok: false, response: error.response };
      // console.log('store', store, 'error ', error);
      if (onFailure != null) {
        onFailure(error.response);
      }
      // A signed-out session already explained itself on the login page.
      if (showErrorMessage !== false && !error.sessionHandled) {  
        errorHandler(error);
      }    
    })
    .finally(() => {
      if (onFinally != null) {
        onFinally();
      }
    });
  if (writeKey) inFlightWrites.set(writeKey, request.then(() => outcome));
  return request;
};
